import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import InputBase from '@mui/material/InputBase';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CloseIcon from '@mui/icons-material/Close';
import { SPECIES_OPTIONS, getSpeciesLabel } from '../utils/treeSpecies';

const formatNumber = (value, digits = 2) => (
  Number.isFinite(value) ? value.toFixed(digits) : '—'
);

/**
 * Renders on-canvas tree popups for editing and tracking tree metadata.
 */
function TreePopups({
  popups = [],
  trackedPopupId,
  t,
  onToggleTracked,
  onDeleteTree,
  onToggleEditMode,
  onClosePopup,
  onFieldChange,
  onRadiusStep
}) {
  if (!popups.length) return null;

  return popups.map((popup) => {
    if (!popup?.screenPosition) return null;
    const isEditing = popup.isEditing;
    const draft = isEditing ? popup.draft : null;

    return (
      <Box
        key={popup.popupId}
        sx={{
          position: 'absolute',
          left: popup.screenPosition.x + 50,
          top: popup.screenPosition.y,
          transform: 'translate(0, -50%)',
          zIndex: 1300,
          bgcolor: 'rgba(20,20,30,0.6)',
          borderRadius: 1,
          px: 1.25,
          py: 1,
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          minWidth: 200,
          maxWidth: 200,
          border: '1px solid rgba(255,255,255,0.08)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5, gap: 0.5 }}>
          <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700 }}>
            {t('TreeId')} {popup.treeId}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
            <IconButton
              size="small"
              onClick={() => onToggleTracked(popup.popupId)}
              sx={{
                color: '#fff',
                p: 0.25,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.1)'
                }
              }}
              title={trackedPopupId === popup.popupId ? (t('StopTracking') || 'Stop tracking') : (t('Track') || 'Track')}
            >
              {trackedPopupId === popup.popupId ? (
                <VideocamOffIcon sx={{ fontSize: '14px' }} />
              ) : (
                <VideocamIcon sx={{ fontSize: '14px' }} />
              )}
            </IconButton>
            {isEditing && (
              <IconButton
                size="small"
                onClick={() => onDeleteTree(popup.popupId)}
                sx={{
                  color: '#fff',
                  p: 0.25,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)'
                  }
                }}
                title={t('Delete') || 'Delete tree'}
              >
                <DeleteOutlineIcon sx={{ fontSize: '14px' }} />
              </IconButton>
            )}
            <IconButton
              size="small"
              onClick={() => onToggleEditMode(popup)}
              sx={{
                color: '#fff',
                p: 0.25,
                bgcolor: isEditing ? 'rgba(76, 175, 80, 0.25)' : 'transparent',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.1)'
                }
              }}
              title={isEditing ? (t('Done') || 'Done editing') : (t('Edit') || 'Edit')}
            >
              {isEditing ? (
                <CheckIcon sx={{ fontSize: '14px' }} />
              ) : (
                <EditIcon sx={{ fontSize: '14px' }} />
              )}
            </IconButton>
            <IconButton
              size="small"
              onClick={() => onClosePopup(popup.popupId)}
              sx={{
                color: '#fff',
                p: 0.25,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.1)'
                }
              }}
              title={t('Close') || 'Close'}
            >
              <CloseIcon sx={{ fontSize: '14px' }} />
            </IconButton>
          </Box>
        </Box>
        <Typography variant="caption" sx={{ color: '#ddd', display: 'block' }}>
          {t('TreeLon')}: {formatNumber(popup.lon, 6)}
        </Typography>
        <Typography variant="caption" sx={{ color: '#ddd', display: 'block' }}>
          {t('TreeLat')}: {formatNumber(popup.lat, 6)}
        </Typography>
        <Typography variant="caption" sx={{ color: '#ddd', display: 'block' }}>
          {t('TreeElevation')}: {formatNumber(popup.elevation, 2)} m
        </Typography>
        {isEditing ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0 }}>
              <Typography variant="caption" sx={{ color: '#ddd', flex: 1 }}>
                {t('TreeRadius')}: {formatNumber(draft?.radius ?? popup.radius, 3)} m
              </Typography>
              <IconButton
                size="small"
                onClick={() => onRadiusStep(popup.popupId, -0.01)}
                sx={{
                  color: '#fff',
                  p: 0.25,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                }}
                title={t('DecreaseRadius') || 'Decrease radius by 1 cm'}
              >
                <RemoveIcon sx={{ fontSize: '14px' }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => onRadiusStep(popup.popupId, 0.01)}
                sx={{
                  color: '#fff',
                  p: 0.25,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                }}
                title={t('IncreaseRadius') || 'Increase radius by 1 cm'}
              >
                <AddIcon sx={{ fontSize: '14px' }} />
              </IconButton>
            </Box>
            <Typography variant="caption" sx={{ color: '#ddd', display: 'block' }}>
              {t('TreeHeight')}: {popup.height !== null && popup.height !== undefined ? `${formatNumber(popup.height, 3)} m` : '—'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <Typography
                variant="caption"
                sx={{ color: '#ddd', flexShrink: 0, minWidth: 70, textAlign: 'left' }}
              >
                {t('Species') || 'Species'}:
              </Typography>
              <Select
                value={draft?.species ?? ''}
                onChange={(event) => onFieldChange(popup.popupId, 'species', event.target.value)}
                displayEmpty
                size="small"
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: 'rgba(20,20,30,0.7)',
                      color: '#fff',
                      '& .MuiMenuItem-root': {
                        color: '#fff'
                      },
                      '& .MuiMenuItem-root.Mui-selected': {
                        bgcolor: 'rgba(255,255,255,0.12)'
                      },
                      '& .MuiMenuItem-root.Mui-selected:hover': {
                        bgcolor: 'rgba(255,255,255,0.18)'
                      }
                    }
                  }
                }}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  width: '100%',
                  height: 24,
                  color: '#fff',
                  fontSize: '0.75rem',
                  '& .MuiSelect-select': {
                    py: 0.5,
                    px: 0.75
                  },
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '& .MuiSelect-icon': { color: '#fff' },
                  backgroundColor: 'rgba(255,255,255,0.08)',
                  borderRadius: 0.5
                }}
              >
                <MenuItem value="">
                  <em>—</em>
                </MenuItem>
                {SPECIES_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {t(opt.labelKey) || opt.fallback || opt.labelKey}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <Typography
                variant="caption"
                sx={{ color: '#ddd', flexShrink: 0, minWidth: 70, textAlign: 'left' }}
              >
                {t('Comment') || 'Comment'}:
              </Typography>
              <InputBase
                value={draft?.comment ?? popup.comment ?? ''}
                onChange={(event) => onFieldChange(popup.popupId, 'comment', event.target.value)}
                placeholder=""
                inputProps={{
                  'aria-label': t('Comment') || 'Comment',
                  sx: { padding: 0 }
                }}
                sx={{
                  flex: 2,
                  minWidth: 0,
                  width: '100%',
                  height: 24,
                  color: '#fff',
                  fontSize: '0.75rem',
                  px: 0.5,
                  py: 0.5,
                  borderRadius: 0.5,
                  border: '1px solid rgba(255, 255, 255, 0)',
                  backgroundColor: 'rgba(255,255,255,0.08)',
                  transition: 'border-color 0.15s ease, background-color 0.15s ease',
                  '&:focus-within': {
                    borderColor: 'rgba(255,255,255,0.0)',
                    backgroundColor: 'rgba(255,255,255,0.1)'
                  }
                }}
              />
            </Box>
            <Box sx={{ px: 1, py: 0.5, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 1, mt: 1 }}>
              <Typography variant="caption" sx={{ color: '#ffffffff', display: 'block' }}>
                {t('RotateHint')}
              </Typography>
            </Box>
          </>
        ) : (
          <>
            <Typography variant="caption" sx={{ color: '#ddd', display: 'block' }}>
              {t('TreeRadius')}: {formatNumber(popup.radius, 3)} m
            </Typography>
            <Typography variant="caption" sx={{ color: '#ddd', display: 'block' }}>
              {t('TreeHeight')}: {popup.height !== null && popup.height !== undefined ? `${formatNumber(popup.height, 3)} m` : '—'}
            </Typography>
            {getSpeciesLabel(popup.species, t) && (
              <Typography variant="caption" sx={{ color: '#ddd', display: 'block' }}>
                {t('Species') || 'Species'}: {getSpeciesLabel(popup.species, t)}
              </Typography>
            )}
            {(popup.comment ?? '').trim() !== '' && (
              <Typography variant="caption" sx={{ color: '#ddd', display: 'block' }}>
                {t('Comment') || 'Comment'}: {popup.comment}
              </Typography>
            )}
          </>
        )}
      </Box>
    );
  });
}

TreePopups.propTypes = {
  popups: PropTypes.arrayOf(PropTypes.shape({
    popupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    treeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    lon: PropTypes.number,
    lat: PropTypes.number,
    elevation: PropTypes.number,
    radius: PropTypes.number,
    height: PropTypes.number,
    species: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    comment: PropTypes.string,
    screenPosition: PropTypes.shape({
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired
    }),
    draft: PropTypes.shape({
      treeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      radius: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      comment: PropTypes.string,
      species: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    }),
    isEditing: PropTypes.bool
  })),
  trackedPopupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  t: PropTypes.func.isRequired,
  onToggleTracked: PropTypes.func.isRequired,
  onDeleteTree: PropTypes.func.isRequired,
  onToggleEditMode: PropTypes.func.isRequired,
  onClosePopup: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onRadiusStep: PropTypes.func.isRequired
};

export default TreePopups;
